/**
 * analytics.js — thin, dependency-free GA4 event helper.
 *
 * Usage:
 *   import { trackEvent, EVENTS } from '../lib/analytics';
 *   trackEvent(EVENTS.GENERATE_LEAD, { form: 'homepage_hero', goal });
 *
 * Design:
 *   - Fires `window.gtag('event', name, params)` when gtag is loaded
 *     (the site injects GA4 via TrackingScriptsInjector).
 *   - Also mirrors to `window.dataLayer.push({ event: name, ...params })`
 *     so GTM-based setups get the same signal — costs nothing.
 *   - No-ops silently when neither is present (SSR, tests, ad-block).
 *   - Never throws. Never blocks. Never awaits.
 *
 * Event vocabulary (kept aligned with GA4 recommended events where they
 * exist so the admin can mark them as *Key Events* in GA4 UI without
 * renaming):
 *   - sign_up        : GA4 recommended, fires after successful registration
 *   - login          : GA4 recommended, fires after successful login
 *   - generate_lead  : GA4 recommended, fires on any lead-capture submit
 *                      (homepage hero, /free hero, /free-score popup)
 *   - checkout_open  : requested by product — Razorpay modal opened
 *   - purchase       : GA4 recommended, fires on successful payment
 *                      (ThankYouPage handles the persistent version)
 *   - checkout_dismissed : fires when the Razorpay modal is closed
 *                          without a completed payment (abandonment)
 *   - cta_click      : catch-all for pricing / marketing clicks
 */

// Canonical event names — import these instead of magic strings.
export const EVENTS = Object.freeze({
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  GENERATE_LEAD: 'generate_lead',
  CHECKOUT_OPEN: 'checkout_open',
  CHECKOUT_DISMISSED: 'checkout_dismissed',
  PURCHASE: 'purchase',
  CTA_CLICK: 'cta_click',
});


function _isBrowser() {
  return typeof window !== 'undefined';
}


/**
 * Fire a GA4 event. Silent no-op if gtag / dataLayer are unavailable.
 *
 * @param {string} name    Event name (prefer values from EVENTS above)
 * @param {object} [params] GA4-style params (snake_case keys). Values
 *                          must be primitives — no functions or DOM refs.
 */
export function trackEvent(name, params = {}) {
  if (!_isBrowser() || !name) return;
  try {
    // Strip nullish so GA4 doesn't ingest "value=null" rows.
    const clean = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v !== null && v !== undefined && v !== '') clean[k] = v;
    });

    // 1) gtag path (GA4 direct config)
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, clean);
    }
    // 2) dataLayer path (GTM setups) — mirror the same signal.
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...clean });
    }
  } catch (_) {
    // never break the UX for analytics
  }
}


/** Convenience — for lead capture forms. */
export function trackLead(formId, extra = {}) {
  trackEvent(EVENTS.GENERATE_LEAD, { form: formId, ...extra });
}


/** Convenience — for pricing / marketing CTAs. */
export function trackCta(ctaId, extra = {}) {
  trackEvent(EVENTS.CTA_CLICK, { cta: ctaId, ...extra });
}
